import { doc, getDoc, setDoc, runTransaction, DocumentReference } from 'firebase/firestore';
import { db } from './firebase';

export interface FavoriteItem {
  type: 'tamkeen' | 'journey' | 'deck';
  id: string;
  title: string;
  classId?: string;
  addedAt?: number;
  [key: string]: unknown;
}

function favRef(uid: string): DocumentReference {
  return doc(db, 'progress', uid, 'favorites', 'list');
}

const VALID_FAV_TYPES = new Set(['tamkeen', 'journey', 'deck']);

function sanitizeFavorites(raw: unknown): FavoriteItem[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((item): item is FavoriteItem => {
    if (!item || typeof item !== 'object') return false;
    if (!VALID_FAV_TYPES.has(item.type)) return false;
    if (typeof item.id !== 'string' || !item.id) return false;
    if (typeof item.title !== 'string') return false;
    return true;
  });
}

export async function getFavorites(uid: string): Promise<FavoriteItem[]> {
  const snap = await getDoc(favRef(uid));
  if (!snap.exists()) return [];
  return sanitizeFavorites(snap.data().items);
}

export async function addFavorite(uid: string, item: Omit<FavoriteItem, 'addedAt'>): Promise<FavoriteItem[]> {
  const ref = favRef(uid);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const current = snap.exists() ? sanitizeFavorites(snap.data().items) : [];
    const exists = current.some(f => f.type === item.type && f.id === item.id);
    if (exists) return current;
    const updated = [...current, { ...item, addedAt: Date.now() }];
    tx.set(ref, { items: updated });
    return updated;
  });
}

export async function removeFavorite(uid: string, type: string, id: string): Promise<FavoriteItem[]> {
  const ref = favRef(uid);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const current = snap.exists() ? sanitizeFavorites(snap.data().items) : [];
    const updated = current.filter(f => !(f.type === type && f.id === id));
    tx.set(ref, { items: updated });
    return updated;
  });
}
